<template>
    <main-layout>
        <inertia-head>
            <title>Manage Users</title>
        </inertia-head>

        <template #header>
            <header-component></header-component>
        </template>

        <div class="py-12">
            <div class="max-w-7xl mx-auto px-4 lg:px-8">
                <div class="bg-white overflow-hidden shadow-sm">
                    <div class="p-6 bg-white border-b border-gray-200">
                        <div class="font-medium text-xl text-secondary mb-2">
                            Manage Users
                        </div>

                        <!-- For Large Screen -->
                        <!-- Column Header -->
                        <div
                            class="
                                hidden
                                lg:grid
                                grid-cols-4
                                p-2
                                font-medium
                                text-gray-500
                                border-b border-gray-200
                            "
                        >
                            <div :class="headerClasses">Name</div>
                            <div :class="headerClasses">Email Address</div>
                            <div :class="headerClasses">Role</div>
                            <div :class="headerClasses">Permissions</div>
                        </div>

                        <!-- User List -->
                        <div
                            class="
                                hidden
                                lg:grid
                                grid-cols-4
                                p-2
                                text-secondary
                            "
                            v-for="user in users"
                            :key="user.id"
                        >
                            <!-- Name -->
                            <div
                                v-if="
                                    selectedUser &&
                                    selectedUser.id === user.id &&
                                    selectedField === 'name'
                                "
                                :class="cellClasses"
                            >
                                <form @submit.prevent="submit">
                                    <input
                                        id="name"
                                        type="text"
                                        v-model="form.value"
                                        :class="inputClasses"
                                        @keydown.esc="cancelForm"
                                        required
                                        autofocus
                                    />
                                    <div class="flex justify-end">
                                        <button
                                            type="submit"
                                            :class="buttonClasses"
                                            class="icon-np-check"
                                        ></button>
                                        <button
                                            type="button"
                                            :class="buttonClasses"
                                            class="icon-np-cancel"
                                            @click="cancelForm"
                                        ></button>
                                    </div>
                                </form>
                            </div>
                            <div
                                :class="cellClasses"
                                @click="editName(user)"
                                v-else
                            >
                                {{ user.name }}
                            </div>

                            <!-- Email -->
                            <div
                                v-if="
                                    selectedUser &&
                                    selectedUser.id === user.id &&
                                    selectedField === 'email'
                                "
                                :class="cellClasses"
                            >
                                <form @submit.prevent="submit">
                                    <input
                                        id="email"
                                        type="email"
                                        v-model="form.value"
                                        :class="inputClasses"
                                        @keydown.esc="cancelForm"
                                        required
                                        autofocus
                                    />
                                    <div class="flex justify-end">
                                        <button
                                            type="submit"
                                            :class="buttonClasses"
                                            class="icon-np-check"
                                        ></button>
                                        <button
                                            type="button"
                                            :class="buttonClasses"
                                            class="icon-np-cancel"
                                            @click="cancelForm"
                                        ></button>
                                    </div>
                                </form>
                            </div>
                            <div
                                :class="cellClasses"
                                @click="editEmail(user)"
                                v-else
                            >
                                {{ user.email }}
                            </div>

                            <!-- Roles -->
                            <div
                                class="inline-grid"
                                :class="cellClasses"
                                @click="editRole(user)"
                            >
                                <span v-if="user.roles.length > 0">
                                    {{
                                        capitalizeFirstLetter(
                                            user.roles[0].name
                                        )
                                    }}
                                </span>
                            </div>

                            <!-- Permissions -->
                            <div
                                class="inline-grid"
                                :class="cellClasses"
                                v-if="user.permissionList.length > 0"
                                @click="editPermission(user)"
                            >
                                <span
                                    v-for="permission in user.permissionList"
                                    :key="permission"
                                >
                                    {{ parsePermission(permission) }}
                                </span>
                            </div>
                            <div
                                v-else
                                class="inline-grid"
                                :class="cellClasses"
                            >
                                <button
                                    class="
                                        w-max
                                        font-medium
                                        text-white
                                        border-2 border-primary
                                        text-center text-xs
                                        py-1.5
                                        px-3
                                        cursor-pointer
                                        rounded-full
                                        bg-primary
                                        hover:bg-secondary
                                        hover:text-white
                                        hover:border-secondary
                                        focus:outline-none
                                        transition-colors
                                        duration-500
                                    "
                                    @click="editPermission(user)"
                                >
                                    Add Permission
                                </button>
                            </div>
                        </div>

                        <!-- For Small Screen -->
                        <div
                            class="lg:hidden p-2 my-4 text-secondary"
                            v-for="user in users"
                            :key="user.id"
                        >
                            <!-- Name -->
                            <div class="flex items-baseline">
                                <div class="flex-shrink font-medium pr-2">
                                    Name
                                </div>
                                <div
                                    v-if="
                                        selectedUser &&
                                        selectedUser.id === user.id &&
                                        selectedField === 'name'
                                    "
                                >
                                    <form @submit.prevent="submit">
                                        <input
                                            id="responsiveName"
                                            type="text"
                                            v-model="form.value"
                                            :class="inputClasses"
                                            required
                                            autofocus
                                        />
                                        <div class="flex justify-end">
                                            <button
                                                type="submit"
                                                :class="buttonClasses"
                                                class="icon-np-check"
                                            ></button>
                                            <button
                                                type="button"
                                                :class="buttonClasses"
                                                class="icon-np-cancel"
                                                @click="cancelForm"
                                            ></button>
                                        </div>
                                    </form>
                                </div>
                                <div
                                    class="text-gray-400"
                                    @click="editName(user)"
                                    v-else
                                >
                                    {{ user.name }}
                                </div>
                            </div>

                            <!-- Email -->
                            <div class="flex items-baseline">
                                <div class="flex-shrink font-medium pr-2">
                                    Email
                                </div>
                                <div
                                    v-if="
                                        selectedUser &&
                                        selectedUser.id === user.id &&
                                        selectedField === 'email'
                                    "
                                    :class="cellClasses"
                                >
                                    <form @submit.prevent="submit">
                                        <input
                                            id="responsiveEmail"
                                            type="email"
                                            v-model="form.value"
                                            :class="inputClasses"
                                            required
                                            autofocus
                                        />
                                        <div class="flex justify-end">
                                            <button
                                                type="submit"
                                                :class="buttonClasses"
                                                class="icon-np-check"
                                            ></button>
                                            <button
                                                type="button"
                                                :class="buttonClasses"
                                                class="icon-np-cancel"
                                                @click="cancelForm"
                                            ></button>
                                        </div>
                                    </form>
                                </div>
                                <div
                                    class="text-gray-400"
                                    @click="editEmail(user)"
                                    v-else
                                >
                                    {{ user.email }}
                                </div>
                            </div>

                            <!-- Role -->
                            <div class="flex items-baseline">
                                <div class="flex-shrink font-medium pr-2">
                                    Role
                                </div>
                                <div
                                    class="text-gray-400"
                                    @click="editRole(user)"
                                >
                                    <span v-if="user.roles.length > 0">
                                        {{
                                            capitalizeFirstLetter(
                                                user.roles[0].name
                                            )
                                        }}
                                    </span>
                                </div>
                            </div>

                            <!-- Permissions -->
                            <div class="my-2 flex items-baseline justify-start">
                                <button
                                    class="
                                        w-max
                                        font-medium
                                        text-white
                                        border-2 border-primary
                                        text-center text-xs
                                        py-1.5
                                        px-3
                                        cursor-pointer
                                        rounded-full
                                        bg-primary
                                    "
                                    @click="editPermission(user)"
                                >
                                    Edit Permission
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- showConfirmationModal -->
        <confirmation-modal
            v-model="showConfirmationModal"
            :message="confirmationModalMsg"
            @confirm="confirmAction"
            @cancel="closeConfirmationModal"
        ></confirmation-modal>

        <!-- showPermissionModal -->
        <permission-modal
            v-model="showPermissionModal"
            :permissions="permissions"
            :user="selectedUser"
            @confirm="confirmPermissionChange"
            @cancel="closePermissionModal"
        ></permission-modal>
    </main-layout>
</template>

<script>
import MainLayout from "@/Layouts/Main";
import HeaderComponent from "@/Components/Dashboard/Header";
import ButtonComponent from "@/Components/Button";
import ConfirmationModal from "@/Components/ConfirmationModal";
import PermissionModal from "@/Components/Manage/PermissionModal";

export default {
    components: {
        MainLayout,
        HeaderComponent,
        ButtonComponent,
        ConfirmationModal,
        PermissionModal,
    },
    props: ["users", "permissions"],
    data() {
        return {
            headerClasses: ["p-2", "flex"],
            cellClasses: [
                "p-2",
                "h-24",
                "flex",
                "items-center",
                "cursor-pointer",
                "hover:text-primary",
                "transition-all",
                "duration-500",
            ],
            inputClasses: [
                "w-full",
                "rounded",
                "px-3",
                "py-2",
                "border",
                "border-gray-300",
                "focus:border-primary",
                "focus:ring-primary",
            ],
            buttonClasses: [
                "ml-2",
                "mt-2",
                "text-2xl",
                "text-secondary",
                "hover:text-primary",
                "transition-colors",
                "duration-500",
                "focus:outline-none",
            ],
            selectedUser: null,
            selectedField: null,
            form: null,
            confirmationModalMsg: null,
            showConfirmationModal: false,
            showPermissionModal: false,
        };
    },
    methods: {
        capitalizeFirstLetter(val) {
            return val.charAt(0).toUpperCase() + val.slice(1);
        },
        parsePermission(permission) {
            return this.capitalizeFirstLetter(permission.replace("-", " "));
        },
        editName(user) {
            this.selectedUser = user;
            this.selectedField = "name";
            this.form = this.$inertia.form({
                field: this.selectedField,
                value: user.name,
            });
        },
        editEmail(user) {
            this.selectedUser = user;
            this.selectedField = "email";
            this.form = this.$inertia.form({
                field: this.selectedField,
                value: user.email,
            });
        },
        editRole(user) {
            // there are only two roles, either admin or user
            this.selectedUser = user;
            this.selectedField = "role";

            const role = this.capitalizeFirstLetter(user.roles[0].name);
            this.confirmationModalMsg =
                "Are you sure to change the role from " + role + " to ";
            switch (role) {
                case "Admin":
                    this.confirmationModalMsg += "User";
                    break;
                case "User":
                    this.confirmationModalMsg += "Admin";
                    break;
                default:
                    break;
            }
            this.confirmationModalMsg += "?";

            this.showConfirmationModal = true;
        },
        editPermission(user) {
            this.selectedUser = user;
            this.selectedField = "permission";
            this.showPermissionModal = true;
        },
        confirmRoleChange() {
            this.$inertia.post(this.selectedUser.id + "/changeRole");
            this.closeConfirmationModal();
        },
        confirmPermissionChange(permissions) {
            this.$inertia.post(this.selectedUser.id + "/changePermission", {
                permissions: permissions,
            });
            this.closePermissionModal();
        },
        cancelForm() {
            this.selectedUser = null;
            this.selectedField = null;
            this.form = null;
        },
        submit() {
            if (this.selectedUser) {
                this.form.post(this.selectedUser.id + "/edit", {
                    onFinish: () => this.cancelForm(),
                });
            }
        },
        confirmAction() {
            switch (this.selectedField) {
                case "role":
                    this.confirmRoleChange();
                    break;
                default:
                    break;
            }
        },
        closeConfirmationModal() {
            this.cancelForm();
            this.confirmationModalMsg = null;
            this.showConfirmationModal = false;
        },
        closePermissionModal() {
            this.cancelForm();
            this.showPermissionModal = false;
        },
    },
};
</script>
